import { motion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Assessment } from './components/Assessment';
import { BoundaryMap } from './components/BoundaryMap';
import { BrandHeader } from './components/BrandHeader';
import { Results } from './components/Results';
import { RoleSelect } from './components/RoleSelect';
import { SharedResult } from './components/SharedResult';
import { completedEvent, track } from './lib/analytics';
import { loadContent, scenariosForMode } from './lib/content';
import { loadPrefs, savePrefs, type Prefs } from './lib/prefs';
import { loadProgress, saveResult } from './lib/progress';
import { parseSharedResult, scoreAssessment } from './lib/scoring';
import type {
  Answer,
  AppContent,
  AssessmentResult,
  LocalProgress,
  Mode,
} from './lib/types';

type View = 'role' | 'assessment' | 'result' | 'map' | 'shared';

const loadingFallbacks = {
  en: {
    label: 'Loading the assessment',
    errorTitle: 'It did not load',
    errorBody: 'The questions and the scoring model have to arrive together.',
    retry: 'Try again',
  },
  'zh-CN': {
    label: '正在载入',
    errorTitle: '没能载入',
    errorBody: '题目和评分模型必须一起到齐。',
    retry: '再试一次',
  },
  es: {
    label: 'Cargando la evaluación',
    errorTitle: 'No se cargó',
    errorBody: 'Las preguntas y el modelo de puntuación tienen que llegar juntos.',
    retry: 'Reintentar',
  },
} as const;

function requestedLanguage(): string | undefined {
  return new URLSearchParams(window.location.search).get('lang') ?? undefined;
}

export default function App() {
  const [content, setContent] = useState<AppContent | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [localeRequest, setLocaleRequest] = useState<string | undefined>(requestedLanguage());
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [mode, setMode] = useState<Mode | null>(null);
  const [sharedResult] = useState(() => parseSharedResult(window.location.search));
  const [view, setView] = useState<View>(() => (sharedResult ? 'shared' : 'role'));
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [awaitingContinue, setAwaitingContinue] = useState(false);
  const [result, setResult] = useState<AssessmentResult | null>(null);
  const [progress, setProgress] = useState<LocalProgress>(() => loadProgress());
  const [prefs, setPrefs] = useState<Prefs>(() => loadPrefs());
  const advanceTimer = useRef<number | null>(null);

  useEffect(() => {
    let active = true;
    setLoadError(false);
    loadContent(localeRequest)
      .then((loaded) => {
        if (!active) return;
        document.documentElement.lang = loaded.locale.htmlLang;
        document.documentElement.dir = loaded.locale.direction;
        document.title = `${loaded.ui.brand.name} — ${loaded.ui.assessment.title}`;
        setContent(loaded);
      })
      .catch(() => active && setLoadError(true));
    return () => {
      active = false;
    };
  }, [localeRequest, loadAttempt]);

  useEffect(() => () => {
    if (advanceTimer.current !== null) window.clearTimeout(advanceTimer.current);
  }, []);

  const scenarios = useMemo(
    () => (content && mode ? scenariosForMode(content.scenarios, mode) : []),
    [content, mode],
  );
  const currentScenario = scenarios[index] ?? null;
  const selectedChoiceId = currentScenario
    ? answers.find((answer) => answer.scenarioId === currentScenario.id)?.choiceId ?? null
    : null;

  function updateLanguage(locale: string) {
    const url = new URL(window.location.href);
    url.searchParams.set('lang', locale);
    window.history.replaceState({}, '', url);
    setLocaleRequest(locale);
  }

  function selectMode(nextMode: Mode) {
    if (advanceTimer.current !== null) window.clearTimeout(advanceTimer.current);
    setMode(nextMode);
    setAnswers([]);
    setIndex(0);
    setResult(null);
    setIsAdvancing(false);
    setAwaitingContinue(false);
    setView('assessment');
    track({ name: 'mode_selected', locale: content?.locale.code ?? 'unknown', mode: nextMode });
  }

  function toggleExplain() {
    setPrefs((current) => savePrefs({ ...current, explain: !current.explain }));
  }

  // Shared by the timed advance and by the reasoning step's continue button, so
  // both paths score and finish an attempt identically.
  function commit(finalAnswers: Answer[]) {
    if (!content || !mode) return;
    setAwaitingContinue(false);
    if (index < scenarios.length - 1) {
      setIndex((value) => value + 1);
      setIsAdvancing(false);
      track({ name: 'scenario_advanced', locale: content.locale.code, mode, position: index + 2 });
      return;
    }
    const scored = scoreAssessment(scenarios, finalAnswers, content.model);
    setResult(scored);
    setProgress((current) => saveResult(current, scored, scenarios));
    track(completedEvent(content.locale.code, mode, scored.total, scored.typeId));
    const url = new URL(window.location.href);
    url.searchParams.set('result', scored.typeId);
    url.searchParams.set('score', String(scored.total));
    url.searchParams.set('p', String(scored.percentile));
    url.searchParams.set('lang', content.locale.code);
    window.history.replaceState({}, '', url);
    setIsAdvancing(false);
    setView('result');
  }

  function selectChoice(choiceId: string) {
    if (!content || !mode || !currentScenario || selectedChoiceId || isAdvancing) return;
    const next = [...answers, { scenarioId: currentScenario.id, choiceId }];
    setAnswers(next);
    if (answers.length === 0) {
      track({ name: 'assessment_started', locale: content.locale.code, mode });
    }
    if (prefs.explain) {
      setAwaitingContinue(true);
      return;
    }
    setIsAdvancing(true);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // A hidden tab throttles timers and animation frames, so a backgrounded
    // attempt advances at once rather than stranding between two questions.
    const pause = document.hidden || reducedMotion ? 0 : 240;
    advanceTimer.current = window.setTimeout(() => commit(next), pause);
  }

  function resetAssessment() {
    if (advanceTimer.current !== null) window.clearTimeout(advanceTimer.current);
    const url = new URL(window.location.href);
    url.searchParams.delete('result');
    url.searchParams.delete('score');
    url.searchParams.delete('p');
    window.history.replaceState({}, '', url);
    setAnswers([]);
    setIndex(0);
    setResult(null);
    setMode(null);
    setIsAdvancing(false);
    setAwaitingContinue(false);
    setView('role');
    window.scrollTo({ top: 0 });
  }

  if (!content) {
    const fallbackKey = localeRequest === 'zh-CN' || localeRequest === 'es' ? localeRequest : 'en';
    const fallback = loadingFallbacks[fallbackKey];
    return (
      <main className="boot">
        <span className="boot-mark" aria-hidden="true" />
        <strong>THE LINE</strong>
        {loadError ? (
          <div>
            <h1>{fallback.errorTitle}</h1>
            <p>{fallback.errorBody}</p>
            <button type="button" onClick={() => setLoadAttempt((value) => value + 1)}>
              {fallback.retry}
            </button>
          </div>
        ) : (
          <p>{fallback.label}</p>
        )}
      </main>
    );
  }

  const dark = view === 'result' || view === 'shared' || view === 'map';

  return (
    <div className={`shell${dark ? ' is-dark' : ''}`} data-view={view}>
      <BrandHeader
        ui={content.ui}
        locales={content.manifest.locales}
        localeCode={content.locale.code}
        onLocaleChange={updateLanguage}
        onHome={resetAssessment}
        showHome={view !== 'role'}
      />
      <motion.div
        key={view}
        className="shell-view"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
      >
        {view === 'role' ? (
          <RoleSelect
            ui={content.ui}
            updatedAt={content.manifest.updatedAt}
            progress={progress}
            explain={prefs.explain}
            onToggleExplain={toggleExplain}
            onSelect={selectMode}
          />
        ) : null}

        {view === 'assessment' && currentScenario && mode ? (
          <Assessment
            ui={content.ui}
            mode={mode}
            scenario={currentScenario}
            index={index}
            total={scenarios.length}
            selectedChoiceId={selectedChoiceId}
            isAdvancing={isAdvancing}
            awaitingContinue={awaitingContinue}
            onSelect={selectChoice}
            onContinue={() => commit(answers)}
          />
        ) : null}

        {view === 'result' && result && mode ? (
          <Results
            content={content}
            mode={mode}
            result={result}
            scenarios={scenarios}
            answers={answers}
            onExplore={() => {
              track({ name: 'boundary_opened', locale: content.locale.code, mode });
              setView('map');
              window.scrollTo({ top: 0 });
            }}
            onRetake={resetAssessment}
          />
        ) : null}

        {view === 'map' ? (
          <BoundaryMap
            content={content}
            initialMode={mode ?? 'student'}
            {...(result ? { onBack: () => setView('result') } : { onTakeTest: resetAssessment })}
          />
        ) : null}

        {view === 'shared' && sharedResult ? (
          <SharedResult
            {...sharedResult}
            ui={content.ui}
            model={content.model}
            onTakeTest={resetAssessment}
          />
        ) : null}
      </motion.div>
    </div>
  );
}
